import ProviderAffiliations from 'components/FindProvider/ProviderAffiliations/ProviderAffiliations';
import ProviderComments from 'components/FindProvider/ProviderComments/ProviderComments';
import { ProviderDetailsBanner } from 'components/FindProvider/ProviderDetailsBanner/ProviderDetailsBanner';
import { ProviderProfessions } from 'components/FindProvider/ProviderProfessions/ProviderProfessions';
import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { NavigationScreenProps } from 'screens/navigation';
import { ID_TYPE_CORPPROV } from 'store/findProvider/constants';
import { RootState } from 'store/types';
import * as authSelectors from 'store/authentication/selectors';
import * as actions from 'store/findProvider/actions';
import * as selectors from 'store/findProvider/selectors';
import * as types from 'store/findProvider/types';
import { Icon } from 'components/Icon';
import { primaryDark } from 'modules/styles/colors';

import { MuiBox, MuiContainer, MuiGrid, MuiCardContent, MuiDivider } from 'theme/material-ui';
import DataLoader from 'components/UI/DataLoader/DataLoader';
import { ProviderLoading, ProviderError, ProviderNoData } from './shared';
import analyticsService, { AnalyticsUserStat } from 'services/AnalyticsService.web';

import {
  MuiRowCard,
  CardColMedia,
  PaddedContainer,
  TextContainer,
  LargeCardText,
  AddressContainer,
  BottomSpacer,
  SmallText,
  GridSpacer,
  MuiRowCardLong,
  CardColMediaLong,
  MuiBoxLong,
  HrBreaker,
  StyledMuiContainer,
  StyledMuiGrid
} from './styled';
import { SectionHeader } from 'components/FindProvider/common';

interface Props extends NavigationScreenProps {
  isFetching: boolean;
  isDfdAuthenticated: boolean;
  getProviderDetails: typeof actions.getProviderDetails;
  resetProviderDetail: typeof actions.resetProviderDetail;
  provider: types.ProviderDetail;
  allAffiliatedHospitals: types.AffiliatedHospital[];
}

const ProviderDetailsScreen = (props: Props) => {
  const {
    isFetching,
    isDfdAuthenticated,
    getProviderDetails,
    resetProviderDetail,
    provider,
    allAffiliatedHospitals
  } = props;
  const { id: providerId } = props.match.params;

  const criteria: types.ProviderDetailCriteria = {
    id: providerId,
    idType: ID_TYPE_CORPPROV
  };

  useEffect(() => {
    resetProviderDetail();
    getProviderDetails(criteria);
    analyticsService.incrementUserStat(AnalyticsUserStat.ProviderPagesSeen);
  }, [providerId]);

  const providerCommentsRef = useRef(null);
  const scrollToProviderComments = () => {
    providerCommentsRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <MuiBox component="main" width="100%">
      <DataLoader
        loading={isFetching}
        data={provider}
        renderLoading={() => <ProviderLoading />}
        renderError={() => <ProviderError />}
        renderNoData={() => <ProviderNoData />}
        renderData={() => (
          <StyledMuiContainer maxWidth={false}>
            <ProviderDetailsBanner
              provider={provider}
              isAuthenticated={isDfdAuthenticated}
              scrollToProviderComments={scrollToProviderComments}
            />
            <MuiContainer maxWidth="lg">
              <StyledMuiGrid container justify="center" alignItems="center" alignContent="center">
                <ProviderProfessions provider={provider} />
                {provider.locations.length === 0 ? null : provider.locations.length > 1 ? (
                  // Multiple Location Cards
                  <MuiGrid item xs={12}>
                    <SectionHeader variant="h5">Locations</SectionHeader>
                    <MuiDivider />
                    <>
                      <GridSpacer>
                        <MuiGrid id="provider-details-locations" container alignItems="stretch">
                          {provider.locations.map(location => (
                            <BottomSpacer xs={12} sm={6} md={6} lg={4}>
                              <MuiRowCard>
                                <MuiBox>
                                  {location.locationImageUrl ? (
                                    <CardColMedia imageUrl={location.locationImageUrl} />
                                  ) : null}
                                  <MuiCardContent>
                                    <PaddedContainer>
                                      <TextContainer>
                                        <LargeCardText>
                                          {location.name ? location.name : null}
                                        </LargeCardText>
                                        {location.address ? (
                                          <AddressContainer>
                                            <MuiGrid item>
                                              <SmallText>
                                                {location.address.addressLine1
                                                  ? `${location.address.addressLine1}, `
                                                  : null}
                                                {location.address.addressLine2
                                                  ? `${location.address.addressLine2}`
                                                  : null}
                                              </SmallText>
                                              <br />
                                              <SmallText>
                                                {location.address.city
                                                  ? `${location.address.city} `
                                                  : null}
                                                {location.address.postalCode
                                                  ? `${location.address.postalCode}`
                                                  : null}
                                              </SmallText>
                                            </MuiGrid>
                                          </AddressContainer>
                                        ) : null}
                                        <AddressContainer>
                                          <SmallText>
                                            {location.distanceToUser
                                              ? `${(
                                                  <Icon
                                                    name="location-on"
                                                    color={primaryDark}
                                                    size={15}
                                                  />
                                                )}Distance: ${parseFloat(
                                                  location.distanceToUser.toFixed(2)
                                                ).toString()} miles`
                                              : null}
                                          </SmallText>
                                        </AddressContainer>
                                        <SmallText>{location.phoneNumber}</SmallText>
                                      </TextContainer>
                                    </PaddedContainer>
                                  </MuiCardContent>
                                </MuiBox>
                              </MuiRowCard>
                            </BottomSpacer>
                          ))}
                        </MuiGrid>
                      </GridSpacer>
                    </>
                  </MuiGrid>
                ) : (
                  // Single Location Card
                  <MuiGrid item xs={9}>
                    <SectionHeader variant="h5">Locations</SectionHeader>
                    <MuiDivider />
                    <>
                      <MuiGrid id="provider-details-locations" container alignItems="stretch">
                        {provider.locations.map(location => (
                          <BottomSpacer xs={12}>
                            <MuiRowCardLong>
                              <MuiBoxLong>
                                {location.locationImageUrl ? (
                                  <CardColMediaLong imageUrl={location.locationImageUrl} />
                                ) : null}
                                <MuiCardContent>
                                  <TextContainer>
                                    <LargeCardText>
                                      {location.name ? location.name : null}
                                    </LargeCardText>
                                    {location.address ? (
                                      <AddressContainer>
                                        <MuiGrid item>
                                          <SmallText>
                                            {location.address.addressLine1
                                              ? `${location.address.addressLine1}, `
                                              : null}
                                            {location.address.addressLine2
                                              ? `${location.address.addressLine2}`
                                              : null}
                                          </SmallText>
                                          <br />
                                          <SmallText>
                                            {location.address.city
                                              ? `${location.address.city} `
                                              : null}
                                            {location.address.postalCode
                                              ? `${location.address.postalCode}`
                                              : null}
                                          </SmallText>
                                        </MuiGrid>
                                      </AddressContainer>
                                    ) : null}
                                    <AddressContainer>
                                      <SmallText>
                                        {location.distanceToUser
                                          ? `${(
                                              <Icon
                                                name="location-on"
                                                color={primaryDark}
                                                size={15}
                                              />
                                            )}Distance: ${parseFloat(
                                              location.distanceToUser.toFixed(2)
                                            ).toString()} miles`
                                          : null}
                                      </SmallText>
                                    </AddressContainer>
                                    <HrBreaker />
                                    <SmallText>{location.phoneNumber}</SmallText>
                                  </TextContainer>
                                </MuiCardContent>
                              </MuiBoxLong>
                            </MuiRowCardLong>
                          </BottomSpacer>
                        ))}
                      </MuiGrid>
                    </>
                  </MuiGrid>
                )}
                {props.allAffiliatedHospitals.length > 0 ? (
                  <ProviderAffiliations affiliations={allAffiliatedHospitals} />
                ) : null}
                <ProviderComments criteria={criteria} providerCommentsRef={providerCommentsRef} />
              </StyledMuiGrid>
            </MuiContainer>
          </StyledMuiContainer>
        )}
      />
    </MuiBox>
  );
};

const mapStateToProps = (state: RootState) => ({
  isFetching: selectors.providerDetailFetchingSelector(state),
  isDfdAuthenticated: authSelectors.isDfdAuthenticatedSelector(state),
  provider: selectors.providerSelector(state),
  allAffiliatedHospitals: selectors.providerAllAffiliatedHospitalsSelector(state)
});

const mapDispatchToProps = {
  getProviderDetails: actions.getProviderDetails,
  resetProviderDetail: actions.resetProviderDetail
};

export default connect(mapStateToProps, mapDispatchToProps)(ProviderDetailsScreen);
